// TypeScript mirror of contracts/puzzle-spec.schema.json (PuzzleSpec v1),
// including the Phase 1 component glyph.swatch@1 (contracts/phase1.md) and the
// Phase 5 components board.pair@1, board.scatter@1, board.cards@1 and
// glyph.jug@1 (contracts/phase5.md), and board.graph@1 (Planning: Road Map).
//
// The renderer treats every field defensively: specs come from the network and
// may contain components newer than this client. Unknown kinds render an
// "Unsupported: <kind>" placeholder instead of throwing.

export type PuzzleColor =
  | "ink"
  | "accent"
  | "ember"
  | "violet"
  | "mint"
  | "sky"
  | "pink"
  | "amber"
  | "orange"
  | "red"
  | "blue"
  | "purple"
  | "yellow"
  | "green"
  | "muted";

export type Category = "pattern" | "number" | "spatial" | "logic" | "memory" | "speed" | "planning" | "word";

/** [row, column], zero-based. */
export type CellRef = [number, number];

export interface TextGlyph {
  kind: "text";
  value: string;
  style?: "number" | "letter" | "word" | "equation" | "label";
  color?: PuzzleColor;
}

export type ShapeName =
  | "circle"
  | "square"
  | "triangle"
  | "star"
  | "diamond"
  | "hexagon"
  | "plus"
  | "cross"
  | "ring"
  | "arrow"
  | "heart"
  | "pentagon";

export interface ShapeGlyph {
  kind: "shape";
  shape: ShapeName;
  color?: PuzzleColor;
  fill?: "solid" | "outline" | "half";
  rotation?: number;
  scale?: number;
  mirrored?: boolean;
}

export interface DotsGlyph {
  kind: "dots";
  rows: number;
  cols: number;
  on: CellRef[];
  color?: PuzzleColor;
}

export interface LinesGlyph {
  kind: "lines";
  segments: [number, number, number, number][];
  color?: PuzzleColor;
}

export interface CellsGlyph {
  kind: "cells";
  rows: number;
  cols: number;
  fills: { at: CellRef; color: PuzzleColor }[];
}

export interface IsoGlyph {
  kind: "iso";
  voxels: [number, number, number][];
  color?: PuzzleColor;
}

/** glyph.swatch@1 (Phase 1): flat colour swatch; `tone` is an OKLCH lightness offset, −0.5…0.5. */
export interface SwatchGlyph {
  kind: "swatch";
  color: PuzzleColor;
  tone: number;
}

/** glyph.jug@1 (Phase 5): a measuring vessel drawn to scale (Water Jug). `label` is not drawn. */
export interface JugGlyph {
  kind: "jug";
  capacity: number;
  fill: number;
  unit?: string;
  label?: string;
  color?: PuzzleColor;
}

export interface StackGlyph {
  kind: "stack";
  items: Glyph[];
  layout?: "row" | "column" | "overlay";
}

export interface SlotGlyph {
  kind: "slot";
}

export interface EmptyGlyph {
  kind: "empty";
}

export type Glyph =
  | TextGlyph
  | ShapeGlyph
  | DotsGlyph
  | LinesGlyph
  | CellsGlyph
  | IsoGlyph
  | StackGlyph
  | SwatchGlyph
  | JugGlyph
  | SlotGlyph
  | EmptyGlyph
  | ClueGlyph
  | CubeGlyph;

export interface GridCell {
  glyph?: Glyph;
  fill?: PuzzleColor;
  lit?: boolean;
  interactive?: boolean;
  region?: number;
}

export interface NoneBoard {
  kind: "none";
}
export interface SingleBoard {
  kind: "single";
  glyph: Glyph;
  caption?: string;
}
export interface SequenceBoard {
  kind: "sequence";
  items: Glyph[];
}
export interface MatrixBoard {
  kind: "matrix";
  rows: number;
  cols: number;
  cells: Glyph[];
}
export interface AnalogyBoard {
  kind: "analogy";
  left: Glyph[];
  right: Glyph[];
}
export interface GridBoard {
  kind: "grid";
  rows: number;
  cols: number;
  cells: GridCell[];
  labels?: boolean;
  /** board.grid@2 (launch types): regions drawn as thick outlines, `label` at the top-left cell (KenKen "6+"). */
  regions?: GridRegion[];
}

/** The boards a board.pair@1 half can be (pairs don't nest). */
export type PairHalf = SingleBoard | SequenceBoard | MatrixBoard | GridBoard;

/**
 * board.pair@1 (Phase 5): two boards side by side, or stacked when `layout` is
 * vertical (Grid Difference, Sliding Tiles). answer.cellSet refs address the
 * RIGHT board only.
 */
export interface PairBoard {
  kind: "pair";
  left: PairHalf;
  right: PairHalf;
  labels?: [string, string];
  layout?: "horizontal" | "vertical";
  connector?: "none" | "arrow";
}

export interface ScatterItem {
  glyph: Glyph;
  /** Centre, as a fraction of the field width. */
  x: number;
  /** Centre, as a fraction of the field height. */
  y: number;
  /** Fraction of the field WIDTH: a shape's side or a text's em size. Default 0.08. */
  size?: number;
  /** Degrees clockwise, added to the glyph's own rotation. */
  rotation?: number;
}

/** board.scatter@1 (Phase 5): glyphs placed freely in a field 1 wide and `aspect` tall (Number Hunt, Count the Target). */
export interface ScatterBoard {
  kind: "scatter";
  /** Field height ÷ width, 0.5–2 (default 1). */
  aspect?: number;
  items: ScatterItem[];
}

export interface SpeakerCard {
  name: string;
  /** The statement. */
  text: string;
  /** Avatar orb preset 0–5 (tokens `color.avatarOrbs`). */
  orb?: number;
}

/** board.cards@1 (Phase 5): speaker cards with a statement each (Truth or Lie), and the rule shown above them. */
export interface CardsBoard {
  kind: "cards";
  cards: SpeakerCard[];
  rule?: string;
}

export interface GraphNode {
  id: string;
  /** Drawn in the node (defaults to `id`). */
  label?: string;
  /** Centre, as a fraction of the field width. */
  x: number;
  /** Centre, as a fraction of the field height. */
  y: number;
  role?: "start" | "goal" | "none";
}

export interface GraphEdge {
  /** Node ids. */
  from: string;
  to: string;
  /** Drawn on the road (Road Map: minutes). */
  weight?: string | number;
  /** One way, from → to: an arrowhead at `to`. */
  directed?: boolean;
}

/** board.graph@1 (Planning, Road Map): nodes at free positions in a field 1 wide and `aspect` tall, joined by straight edges. */
export interface GraphBoard {
  kind: "graph";
  /** Field height ÷ width, 0.5–2. */
  aspect?: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export type Board =
  | NoneBoard
  | SingleBoard
  | SequenceBoard
  | MatrixBoard
  | AnalogyBoard
  | GridBoard
  | PairBoard
  | ScatterBoard
  | CardsBoard
  | GraphBoard
  | SceneBoard
  | HiveBoard
  | WheelBoard;

export interface WatchPhase {
  kind: "watch";
  durationMs: number;
  board?: Board;
}
export interface RecallPhase {
  kind: "recall";
}
export type Phase = WatchPhase | RecallPhase;

export interface ChoiceOption {
  id: string;
  glyph: Glyph;
}

export interface ChoiceAnswerSpec {
  kind: "choice";
  layout: "row4" | "row5" | "grid2x2" | "column";
  options: ChoiceOption[];
}
export interface CellSetAnswerSpec {
  kind: "cellSet";
  count: number;
}
export type AnswerSpec =
  | ChoiceAnswerSpec
  | CellSetAnswerSpec
  | NumberAnswerSpec
  | DigitsAnswerSpec
  | FillAnswerSpec
  | SequenceAnswerSpec
  | RowsAnswerSpec;

export interface Check {
  alg: "sha256";
  digest: string;
}

export interface PuzzleSpec {
  spec: 1;
  id: string;
  type: string;
  typeVersion?: number;
  category: Category | string;
  requires: string[];
  difficulty: { score: number; tier?: number };
  timeBudgetMs: number;
  prompt: { text: string; detail?: string };
  palette?: string;
  board: Board;
  phases?: Phase[];
  answer: AnswerSpec;
  accessibility?: {
    boardDescription?: string;
    optionDescriptions?: Record<string, string>;
    /** Launch types: a colour-only type (Stroop, Color Match), never served with `pref.colorblind@1`. */
    colorOnly?: boolean;
  };
  /** Launch types: optional client affordances. `scratch` offers a sketch layer on the board (never submitted). */
  hints?: { scratch?: boolean };
  check?: Check;
}

/**
 * A server-side answer (contracts/README.md "Answers"), and the player's selection in play mode. Launch
 * kinds (contracts/launch-types.md §1) carry their stored form: number, digits and rows as strings, fill
 * as integers in `answer.cells` order, sequence as [r, c] pairs IN TAP ORDER. While a player is still
 * typing, a selection can be partial: fill has `null` for an empty blank, rows `_` for an unanswered row,
 * fewer digits or taps than needed. `answerProgress` says when it is complete; only then submit it (a
 * stored or revealed answer is always complete).
 */
export type Answer =
  | { kind: "choice"; value: string }
  | { kind: "cellSet"; value: CellRef[] }
  | NumberAnswer
  | DigitsAnswer
  | FillAnswer
  | SequenceAnswer
  | RowsAnswer;

export type PhaseName = "watch" | "recall";

/** Reads `kind` from anything without trusting its shape. */
export function kindOf(value: unknown): string {
  if (value && typeof value === "object" && "kind" in value) {
    const kind = (value as { kind: unknown }).kind;
    return typeof kind === "string" ? kind : String(kind);
  }
  return value === undefined ? "undefined" : value === null ? "null" : typeof value;
}

// ---------------------------------------------------------------- launch types (contracts/launch-types.md §1)

/** glyph.clue@1: a Kakuro clue cell split diagonally, `across` top-right, `down` bottom-left. */
export interface ClueGlyph {
  kind: "clue";
  across?: number;
  down?: number;
}

/** One visible face of glyph.cube@1: a colour and an optional text or shape symbol. */
export interface CubeFace {
  color: PuzzleColor;
  symbol?: TextGlyph | ShapeGlyph;
}

/** glyph.cube@1: an isometric cube showing its top, front (left) and right faces (Cube Net, Cube Roll). */
export interface CubeGlyph {
  kind: "cube";
  top: CubeFace;
  front: CubeFace;
  right: CubeFace;
}

/** A board.grid@2 region. */
export interface GridRegion {
  cells: CellRef[];
  label?: string;
  color?: PuzzleColor;
}

/** A board.scene@1 item: a scatter item plus the layer a generator may swap (not drawn). */
export interface SceneItem extends ScatterItem {
  layer?: string;
}

export interface ScenePanel {
  /** Field height ÷ width, 0.5–2 (default 0.62, a landscape picture). */
  aspect?: number;
  items: SceneItem[];
}

/** board.scene@1 (Scene Difference): two scenes, stacked in portrait by default. */
export interface SceneBoard {
  kind: "scene";
  panels: [ScenePanel, ScenePanel];
  labels?: [string, string];
  layout?: "vertical" | "horizontal";
}

/** board.hive@1 (Letter Hive): `center` in the accent, ringed by six letters clockwise from the top. */
export interface HiveBoard {
  kind: "hive";
  center: string;
  letters: string[];
}

/** board.wheel@1 (Word Wheel): 3–9 letters clockwise from the top around a hub label such as "×1". */
export interface WheelBoard {
  kind: "wheel";
  hub: string;
  letters: string[];
}

/** answer.number@1: an integer typed on the keypad. */
export interface NumberAnswerSpec {
  kind: "number";
  min?: number;
  max?: number;
  maxDigits?: number;
  allowNegative?: boolean;
}
/** answer.digits@1: exactly `length` digits typed into slots (Digit Span). */
export interface DigitsAnswerSpec {
  kind: "digits";
  length: number;
}
/** answer.fill@1: every blank (`cells`, in answer order) takes an integer in min..max. */
export interface FillAnswerSpec {
  kind: "fill";
  cells: CellRef[];
  min: number;
  max: number;
}
/** answer.sequence@1: `count` ordered taps on `cells` (default: every cell). */
export interface SequenceAnswerSpec {
  kind: "sequence";
  count: number;
  cells?: CellRef[];
  stopOnError?: boolean;
}
/** answer.rows@1: one Yes/No per row, top to bottom. */
export interface RowsAnswerSpec {
  kind: "rows";
  count: number;
}

export type NumberAnswer = { kind: "number"; value: string };
export type DigitsAnswer = { kind: "digits"; value: string };
/** `null` marks a blank not typed yet (a selection in progress, never a stored answer). */
export type FillAnswer = { kind: "fill"; value: (number | null)[] };

/** The player's selection in play mode: an Answer, possibly still being typed (see `Answer`). */
export type Selection = Answer;
export type SequenceAnswer = { kind: "sequence"; value: CellRef[] };
export type RowsAnswer = { kind: "rows"; value: string };
